/**
 * @file issues.ts
 * @description Коды и уровни замечаний «Сайт → SEO → Проверка сайта» и окна
 * публикации страницы (phase19.16, партия Р3в, v0.19.64; site-builder.md
 * SB132, SB127, SB49; seo.md SEO12; db-design-r3v §6.1). Один реестр для api
 * (аудит) и кабинета (тексты групп): новый вид замечания — строка здесь,
 * а не ветка в двух местах.
 *
 * Все замечания — предупреждения, не блокировки (SB132): уровень `warning`
 * (!) или `info` (ⓘ). «Сайт закрыт от поисковиков» — `warning` с признаком
 * `banner`: кабинет показывает его красной полосой над списком. Строки «всё
 * хорошо» (✓) кодов не имеют — кабинет рисует их, когда группа пуста.
 */

import { deepFreeze } from "../site-theme/deep-freeze.js";

export const SITE_SEO_ISSUE_LEVELS = ["warning", "info"] as const;
export type SiteSeoIssueLevel = (typeof SITE_SEO_ISSUE_LEVELS)[number];

export const SITE_SEO_ISSUE_CODES = [
  "seo_site_noindex",
  "seo_title_duplicate",
  "seo_title_long",
  "seo_description_empty",
  "seo_description_long",
  "seo_h1_count",
  "seo_image_alt_missing",
  "site_menu_target_hidden",
  "seo_link_broken",
  "seo_link_via_redirect",
  "seo_section_target_hidden",
  "seo_redirects_inactive",
  "seo_keyword_missing",
  "seo_keyword_duplicate",
] as const;
export type SiteSeoIssueCode = (typeof SITE_SEO_ISSUE_CODES)[number];

export interface SiteSeoIssueMeta {
  readonly level: SiteSeoIssueLevel;
  /** Заголовок группы языком владельца. */
  readonly title: string;
  /** Что делать — одна фраза под заголовком. */
  readonly hint: string;
  /** Показывать полосой над списком (SB132: «Сайт закрыт от поисковиков»). */
  readonly banner?: true;
}

/** Реестр замечаний: уровень и тексты (SB132, SEO12). */
export const SITE_SEO_ISSUES: Readonly<Record<SiteSeoIssueCode, SiteSeoIssueMeta>> = deepFreeze<
  Record<SiteSeoIssueCode, SiteSeoIssueMeta>
>({
  seo_site_noindex: {
    level: "warning",
    title: "Сайт закрыт от поисковиков",
    hint: "В настройках SEO запрещена индексация — Яндекс и Google не покажут сайт в поиске.",
    banner: true,
  },
  seo_title_duplicate: {
    level: "warning",
    title: "Одинаковый заголовок для поиска",
    hint: "У разных страниц один заголовок — поиск не поймёт, какую показывать. Сделайте заголовки разными.",
  },
  seo_title_long: {
    level: "warning",
    title: "Заголовок длиннее 60 знаков",
    hint: "Поиск обрежет заголовок — главное поставьте в начало или сократите.",
  },
  seo_description_empty: {
    level: "info",
    title: "Нет описания для поиска",
    hint: "Подставится описание сайта. Своё описание поможет выделиться в выдаче.",
  },
  seo_description_long: {
    level: "warning",
    title: "Описание длиннее 160 знаков",
    hint: "Поиск обрежет описание — сократите до 160 знаков.",
  },
  seo_h1_count: {
    level: "warning",
    title: "Главных заголовков H1 не один",
    hint: "На странице должен быть ровно один главный заголовок H1.",
  },
  seo_image_alt_missing: {
    level: "warning",
    title: "Фото без описания",
    hint: "Опишите фото в блоке или в медиатеке — описание читают поиск и программы для незрячих.",
  },
  site_menu_target_hidden: {
    level: "warning",
    title: "Пункт меню ведёт на скрытую страницу",
    hint: "Покажите страницу или поменяйте пункт меню.",
  },
  seo_link_broken: {
    level: "warning",
    title: "Ссылка ведёт в никуда",
    hint: "По внутренней ссылке меню или кнопки открывается «Страница не найдена». Поменяйте адрес.",
  },
  seo_link_via_redirect: {
    level: "info",
    title: "Ссылка идёт через переадресацию",
    hint: "Замените ссылку на новый адрес — так быстрее для посетителя и поиска.",
  },
  seo_section_target_hidden: {
    level: "warning",
    title: "Кнопка или плитка ведёт на скрытую цель",
    hint: "Цель снята с сайта — покажите её или поменяйте ссылку.",
  },
  seo_redirects_inactive: {
    level: "info",
    title: "Переадресации не действуют",
    hint: "Цель скрыта или удалена, или адрес занят живой страницей — откройте список переадресаций.",
  },
  seo_keyword_missing: {
    level: "info",
    title: "Главного запроса нет в заголовке и H1",
    hint: "Добавьте главный запрос в заголовок для поиска или в главный заголовок страницы.",
  },
  seo_keyword_duplicate: {
    level: "info",
    title: "Один главный запрос у двух адресов",
    hint: "Страницы борются за один запрос — выберите для каждой свой.",
  },
});

/** Уровень замечания по коду. */
export function siteSeoIssueLevel(code: SiteSeoIssueCode): SiteSeoIssueLevel {
  return SITE_SEO_ISSUES[code].level;
}
